import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./JournalView.css";
import PremenstrualReportModal from "../PremenstrualReportModal/PremenstrualReportModal";

const SYMPTOM_CATEGORIES = [
  { label: "categoryPain",  emoji: "💢", symptoms: ["crampes", "mal_de_tete", "mal_de_dos", "seins_sensibles"] },
  { label: "categoryMood",    emoji: "🧠", symptoms: ["irritabilite", "anxiete", "depression", "sautes_humeur"] },
  { label: "categoryPhysical",  emoji: "🌿", symptoms: ["fatigue", "ballonnements", "nausees", "acne"] },
];

const MOODS        = ["😞", "😕", "😐", "🙂", "😊"];
const MOOD_LABELS  = ["moodVeryBad", "moodBad", "moodNeutral", "moodGood", "moodExcellent"];
const FLOW_OPTIONS = ["none", "light", "normal", "heavy", "very_heavy"];
const FLOW_LABELS  = { none: "flowNone", light: "flowLight", normal: "flowNormal", heavy: "flowHeavy", very_heavy: "flowVeryHeavy" };
const FLOW_COLORS  = { none: "#e5e7eb", light: "#fca5a5", normal: "#f87171", heavy: "#ef4444", very_heavy: "#b91c1c" };

const PHASE_CONFIG = {
  menstrual:  { accent: "#ef4444", bg: "#fee2e2", color: "#dc2626", label: "phaseMenstrual",    emoji: "🩸" },
  follicular: { accent: "#f59e0b", bg: "#fef9c3", color: "#b45309", label: "phaseFollicular", emoji: "🌱" },
  ovulation:  { accent: "#22c55e", bg: "#dcfce7", color: "#15803d", label: "phaseOvulation",    emoji: "🌸" },
  luteal:     { accent: "#8b5cf6", bg: "#ede9fe", color: "#6d28d9", label: "phaseLuteal",       emoji: "🌙" },
};

function PainBar({ value }) {
  const { t } = useTranslation("cycle");
  if (value == null) return null;
  const pct   = (value / 10) * 100;
  const color = value <= 3 ? "#22c55e" : value <= 6 ? "#f59e0b" : "#ef4444";
  return (
    <div className="jv-pain-wrap">
      <span className="jv-pain-lbl">{t("cycleBot.journalView.pain")}</span>
      <div className="jv-pain-track">
        <div className="jv-pain-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}88, ${color})` }} />
      </div>
      <span className="jv-pain-num" style={{ color }}>{value}/10</span>
    </div>
  );
}

function SymptomModal({ onSave, onClose }) {
  const { t, i18n } = useTranslation("cycle");
  const locale = i18n.resolvedLanguage || i18n.language;
  const [sel,   setSel]   = useState([]);
  const [pain,  setPain]  = useState(0);
  const [nrj,   setNrj]   = useState(3);
  const [mood,  setMood]  = useState(3);
  const [flow,  setFlow]  = useState("none");
  const [notes, setNotes] = useState("");

  const toggle = (s) => setSel((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  return (
    <div className="jv-overlay">
      <div className="jv-modal">
        <div className="jv-modal-hd">
          <div className="jv-modal-title">
            <span>📓</span>
            <div>
              <h2>{t("cycleBot.journalView.dailyNote")}</h2>
              <p>{new Date().toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}</p>
            </div>
          </div>
          <button className="jv-close" onClick={onClose}>✕</button>
        </div>

        <div className="jv-modal-bd">
          <div className="jv-msec">
            <h3>{t("cycleBot.journalView.symptoms")}</h3>
            {SYMPTOM_CATEGORIES.map((cat) => (
              <div key={cat.label} className="jv-cat">
                <span className="jv-cat-hd">{cat.emoji} {t(`cycleBot.journalView.${cat.label}`)}</span>
                <div className="jv-chips">
                  {cat.symptoms.map((s) => (
                    <button key={s} className={`jv-chip ${sel.includes(s) ? "on" : ""}`} onClick={() => toggle(s)}>
                      {t(`cycleBot.journalView.symptom.${s}`, { defaultValue: s.replace(/_/g, " ") })}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="jv-msec">
            <h3>{t("cycleBot.journalView.painLevel")}</h3>
            <div className="jv-slide-row">
              <span>😌</span>
              <input type="range" min="0" max="10" value={pain} onChange={(e) => setPain(+e.target.value)} className="jv-range jv-range--pain" />
              <span>😫</span>
              <span className="jv-badge" style={{ background: pain <= 3 ? "#dcfce7" : pain <= 6 ? "#fef9c3" : "#fee2e2", color: pain <= 3 ? "#15803d" : pain <= 6 ? "#b45309" : "#dc2626" }}>{pain}/10</span>
            </div>
          </div>

          <div className="jv-msec">
            <h3>{t("cycleBot.journalView.energyLevel")}</h3>
            <div className="jv-slide-row">
              <span>🪫</span>
              <input type="range" min="1" max="5" value={nrj} onChange={(e) => setNrj(+e.target.value)} className="jv-range jv-range--energy" />
              <span>⚡</span>
              <span className="jv-badge" style={{ background: "#f0fdf4", color: "#15803d" }}>{nrj}/5</span>
            </div>
          </div>

          <div className="jv-msec">
            <h3>{t("cycleBot.journalView.mood")}</h3>
            <div className="jv-moods">
              {MOODS.map((m, i) => (
                <button key={i} className={`jv-mood ${mood === i+1 ? "on" : ""}`} onClick={() => setMood(i+1)} title={t(`cycleBot.journalView.${MOOD_LABELS[i]}`)}>
                  <span className="jv-mood-em">{m}</span>
                  <span className="jv-mood-lbl">{t(`cycleBot.journalView.${MOOD_LABELS[i]}`)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="jv-msec">
            <h3>{t("cycleBot.journalView.flowIntensity")}</h3>
            <div className="jv-flows">
              {FLOW_OPTIONS.map((f) => (
                <button key={f}
                  className={`jv-flow ${flow === f ? "on" : ""}`}
                  style={flow === f ? { background: FLOW_COLORS[f], color: f === "none" ? "#374151" : "#fff", borderColor: FLOW_COLORS[f] } : {}}
                  onClick={() => setFlow(f)}
                >
                  {f !== "none" && <span className="jv-flow-dot" style={{ background: FLOW_COLORS[f] }} />}
                  {t(`cycleBot.journalView.${FLOW_LABELS[f]}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="jv-msec">
            <h3>{t("cycleBot.journalView.freeNotes")}</h3>
            <textarea className="jv-ta" rows={3} placeholder={t("cycleBot.journalView.freeNotesPlaceholder")} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="jv-modal-ft">
          <button className="jv-btn-cancel" onClick={onClose}>{t("cycleBot.journalView.cancel")}</button>
          <button className="jv-btn-save" onClick={() => onSave({ entry_date: new Date().toISOString().split("T")[0], symptoms: sel, pain_level: pain, energy_level: nrj, mood_score: mood, flow_intensity: flow, notes })}>
            💾 {t("cycleBot.journalView.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function EntryCard({ entry, idx }) {
  const { t, i18n } = useTranslation("cycle");
  const locale = i18n.resolvedLanguage || i18n.language;
  const ph      = PHASE_CONFIG[entry.phase] || { accent: "#9ca3af", bg: "#f3f4f6", color: "#6b7280", label: "—", emoji: "📅" };
  const d       = entry.entry_date ? new Date(entry.entry_date + "T00:00:00") : null;
  const sympts  = Array.isArray(entry.symptoms) ? entry.symptoms : [];
  const mood    = entry.mood_score ? MOODS[entry.mood_score - 1] : null;

  return (
    <div className="jv-card" style={{ animationDelay: `${idx * 55}ms` }}>
      <div className="jv-card-accent" style={{ background: ph.accent }} />

      <div className="jv-card-date">
        <span className="jv-card-day">{d ? d.getDate() : "—"}</span>
        <span className="jv-card-mon">{d ? d.toLocaleDateString(locale, { month: "short" }) : ""}</span>
        <span className="jv-card-wday">{d ? d.toLocaleDateString(locale, { weekday: "short" }) : ""}</span>
      </div>

      <div className="jv-card-main">
        <div className="jv-card-row1">
          <span className="jv-phase-tag" style={{ background: ph.bg, color: ph.color }}>
            {ph.emoji} {t(`cycleBot.journalView.${ph.label}`, { defaultValue: ph.label })}
          </span>
          {mood && <span className="jv-card-mood" title={`${t("cycleBot.journalView.mood")} ${entry.mood_score}/5`}>{mood}</span>}
        </div>

        {entry.pain_level != null && <PainBar value={entry.pain_level} />}

        <div className="jv-card-pills">
          {entry.energy_level != null && (
            <span className="jv-pill" style={{ "--c": "#f59e0b" }}>⚡ {entry.energy_level}/5</span>
          )}
          {entry.mood_score != null && (
            <span className="jv-pill" style={{ "--c": "#8b5cf6" }}>🧠 {entry.mood_score}/5</span>
          )}
          {entry.flow_intensity && entry.flow_intensity !== "none" && (
            <span className="jv-pill" style={{ "--c": FLOW_COLORS[entry.flow_intensity] }}>
              🩸 {t(`cycleBot.journalView.${FLOW_LABELS[entry.flow_intensity]}`)}
            </span>
          )}
        </div>

        {sympts.length > 0 && (
          <div className="jv-card-sympts">
            {sympts.slice(0, 5).map((s) => (
              <span key={s} className="jv-sympt-chip">{t(`cycleBot.journalView.symptom.${s}`, { defaultValue: s.replace(/_/g, " ") })}</span>
            ))}
            {sympts.length > 5 && <span className="jv-sympt-chip jv-more">+{sympts.length - 5}</span>}
          </div>
        )}

        {entry.notes && (
          <p className="jv-card-note"><span>✏️</span> {entry.notes}</p>
        )}
      </div>
    </div>
  );
}

export default function JournalView({ journalData, entries = [], entriesLoading, onLogEntry, premenstrualWindow = null, pmReport = null, pmReportLoading = false, onRequestPMReport }) {
  const { t, i18n } = useTranslation("cycle");
  const locale = i18n.resolvedLanguage || i18n.language;
  const [showModal, setShowModal] = useState(false);
  const [showPMReport, setShowPMReport] = useState(false);

  // Auto-ouvrir le modal quand le rapport arrive
  React.useEffect(() => {
    if (pmReport && !pmReportLoading) setShowPMReport(true);
  }, [pmReport, pmReportLoading]);

  const handleSave = async (payload) => { await onLogEntry(payload); setShowModal(false); };

  const hasEntries    = Array.isArray(entries) && entries.length > 0;
  const topSymps      = journalData?.top_symptoms || [];
  const sortedEntries = [...entries].sort((a, b) => (b.entry_date || "").localeCompare(a.entry_date || ""));

  return (
    <div className="jv-view cb-journal-theme">

      {/* Bannière pré-menstruelle */}
      {premenstrualWindow?.inWindow && (
        <div className="jv-pmb">
          <div className="jv-pmb-left">
            <span className="jv-pmb-ico">🌙</span>
            <div>
              <div className="jv-pmb-title">{t("cycleBot.journalView.premenstrualWindowDay", { day: premenstrualWindow.dayInWindow })}</div>
              <div className="jv-pmb-sub">
                {t("cycleBot.journalView.nextPeriodIn", { count: premenstrualWindow.daysLeft })}
                {" "}· {new Date(premenstrualWindow.nextJ1Date).toLocaleDateString(locale, { day: "numeric", month: "long" })}
              </div>
            </div>
          </div>
          <div className="jv-pmb-track">
            {[1,2,3,4,5,6,7].map((d) => (
              <div key={d} className={`jv-pmb-dot ${d < premenstrualWindow.dayInWindow ? "done" : d === premenstrualWindow.dayInWindow ? "now" : "next"}`}>
                {d < premenstrualWindow.dayInWindow ? "✓" : d}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button className="jv-pmb-btn" onClick={() => setShowModal(true)}>📝 {t("cycleBot.journalView.fillToday")}</button>
            <button
              className="jv-pmb-btn jv-pmb-btn--report"
              onClick={() => {
                if (!pmReport) onRequestPMReport?.();
                setShowPMReport(true);
              }}
              disabled={pmReportLoading}
            >
              {pmReportLoading ? `⏳ ${t("cycleBot.journalView.analyzing")}` : `📋 ${t("cycleBot.journalView.viewAnalysis")}`}
            </button>
          </div>
        </div>
      )}



      {/* Header */}
      <div className="jv-head">
        <div>
          <h1 className="jv-title">📓 {t("cycleBot.journalView.title")}</h1>
          <p className="jv-sub">{t("cycleBot.journalView.subtitle")}</p>
        </div>
        {hasEntries && (
          <div className="jv-head-stats">
            <div className="jv-hstat"><span className="jv-hstat-n">{entries.length}</span><span className="jv-hstat-l">{t("cycleBot.journalView.entries")}</span></div>
            {journalData?.avg_wellbeing != null && (
              <div className="jv-hstat"><span className="jv-hstat-n">{journalData.avg_wellbeing.toFixed(1)}</span><span className="jv-hstat-l">{t("cycleBot.journalView.wellbeing")}</span></div>
            )}
          </div>
        )}
      </div>

      {/* Top symptômes */}
      {topSymps.length > 0 && (
        <div className="jv-topsympts">
          <span className="jv-toplab">{t("cycleBot.journalView.frequent")}</span>
          {topSymps.slice(0, 6).map((s) => <span key={s} className="jv-topchip">{t(`cycleBot.journalView.symptom.${s}`, { defaultValue: s.replace(/_/g, " ") })}</span>)}
        </div>
      )}

      {/* États */}
      {entriesLoading && (
        <div className="jv-loading"><div className="jv-spin" /><p>{t("cycleBot.journalView.loading")}</p></div>
      )}

      {!entriesLoading && !hasEntries && (
        <div className="jv-empty">
          <div className="jv-empty-ico">📓</div>
          <h3>{t("cycleBot.journalView.emptyTitle")}</h3>
          <p>{t("cycleBot.journalView.emptySubtitle")}</p>
        </div>
      )}

      {!entriesLoading && hasEntries && (
        <div className="jv-list">
          {sortedEntries.map((entry, i) => (
            <EntryCard key={entry.id || entry.entry_date || i} entry={entry} idx={i} />
          ))}
        </div>
      )}

      {showModal && <SymptomModal onSave={handleSave} onClose={() => setShowModal(false)} />}

      {showPMReport && (
        <PremenstrualReportModal
          report={pmReport}
          loading={pmReportLoading}
          onClose={() => setShowPMReport(false)}
        />
      )}
    </div>
  );
}
